// Default axios import removed as it's unused
import { Fragment, useEffect, useMemo, useState } from 'react';

import { useAuth } from '../../hooks/useAuth';
import useFetch from '../../hooks/useFetch';
import useSimpleMutation from '../../hooks/useSimpleMutation';
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	ErrorMessage,
	LoadingGif,
	LoadingMessage,
} from '../Shared/CommonStyles';
// Pagination import removed as it's no longer used here

import * as S from './AlbumGrid.styles';
import SlideshowThumbnail from './SlideshowThumbnail';

// Helper component for individual thumbnail loading
const RETRY_DELAY_MS = 2500; // one automatic retry for transient network blips
const ThumbnailImage = ({ src, alt }: { src?: string; alt: string }) => {
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		if (
			src &&
			src !== 'https://placehold.co/400x300/eee/ccc?text=No+Image'
		) {
			setIsLoading(true);
			setHasError(false);
			setAttempt(0);
		} else {
			setIsLoading(false);
		}
	}, [src]);

	const finalSrc = hasError || !src
			? 'https://placehold.co/400x300/eee/ccc?text=No+Image'
			: src;

	return (
		<S.AlbumThumbnail>
			{isLoading && <LoadingGif size="large" />}
			<S.AlbumThumbnailImage
				key={attempt} // remount on retry: same URL, fresh request
				src={finalSrc}
				alt={alt}
				$isLoading={isLoading}
				onLoad={() => setIsLoading(false)}
				onError={() => {
					if (attempt < 1 && src && !hasError) {
						// One automatic retry after a short cooldown before
						// giving up: cold-cache loads burst ~40 image requests
						// and a transient connection failure (e.g. a saturated
						// connection pool or a QUIC/UDP flap) should not
						// permanently replace a real photo with the placeholder.
						setTimeout(() => setAttempt((a) => a + 1), RETRY_DELAY_MS);
						return;
					}
					setIsLoading(false);
					setHasError(true);
				}}
			/>
		</S.AlbumThumbnail>
	);
};

// Local type definitions
interface Album {
	_id: string;
	name: string;
	sample_image: string;
	upload_date: string; // Assuming string based on usage with new Date()
	picture_count: number;
	schoolYear?: string;
	isProcessing?: boolean;
}

interface MediaItem {
	thumbnail_path: string;
}

interface AlbumGridProps {
	category: string;
	onAlbumSelect: (albumId: string, albumName: string) => void;
	// Props for lifted pagination state
	page: number;
	pageSize: number;
	sortField: string;
	sortDirection: string;
	seed: number; // Add seed for random sorting
	// onPageChange prop removed
	// onPageSizeChange prop removed
	onPageCountChange: (newCount: number) => void;
	minYear?: string;
	maxYear?: string;
	onIsLoadingChange?: (isLoading: boolean) => void; // New prop
	showAllPrivatePseudoAlbum?: boolean; // Show "Totes les fotos privades" pseudo-album
}

// Note: We've removed the special styling for the "All Photos" card
// and now it uses the same styling as regular album cards

const AlbumGrid: React.FC<AlbumGridProps> = ({
	category,
	onAlbumSelect,
	// Destructure pagination props
	page,
	pageSize,
	sortField,
	sortDirection,
	seed, // Destructure seed
	// onPageChange destructuring removed
	// onPageSizeChange destructuring removed
	onPageCountChange,
	minYear,
	maxYear,
	onIsLoadingChange, // Destructure new prop
	showAllPrivatePseudoAlbum, // Destructure new prop
}) => {
	const { user } = useAuth();
	// Removed local state for page, pageSize, sortDirection
	const [randomThumbnailUrls, setRandomThumbnailUrls] = useState<string[]>([]);
	// State to hold the total count of items in the category
	const [categoryTotalCount, setCategoryTotalCount] = useState<number | null>(
		null
	);

	// Check if user is authorized to manage albums (create/delete) in this category
	const isAuthenticated =
		!!user &&
		!!category &&
		(user.roles.includes('Admin') ||
			user.roles.includes('Agrupament') ||
			user.roles.includes('Caps') || // trusted curators: any unitat
			user.unitat === category);

	// Construct URL with filters
	const buildUrl = () => {
		if (category === 'secret') {
			let url = `/api/galeria/albums/secret/u/tots?page=${page}&size=${pageSize}&sortField=${sortField}&sortDirection=${sortDirection}&seed=${seed}`;
			if (minYear) url += `&minYear=${minYear}`;
			if (maxYear) url += `&maxYear=${maxYear}`;
			return url;
		}
		if (category === 'classificar') {
			let url = `/api/galeria/albums/per-classificar?page=${page}&size=${pageSize}&sortField=${sortField}&sortDirection=${sortDirection}&seed=${seed}`;
			if (minYear) url += `&minYear=${minYear}`;
			if (maxYear) url += `&maxYear=${maxYear}`;
			return url;
		}
		let url = `/api/galeria/albums/u/${category}?page=${page}&size=${pageSize}&sortField=${sortField}&sortDirection=${sortDirection}&seed=${seed}`;
		if (minYear) url += `&minYear=${minYear}`;
		if (maxYear) url += `&maxYear=${maxYear}`;
		return url;
	};

	// Fetch albums for the selected category
	const {
		data: albumData,
		isLoading,
		error,
	} = useFetch<{ albums: Album[]; pageCount: number }>({
		// Use local Album type
		// Use 'all' as sentinel value instead of empty string to prevent query key validation failure
		queryKey: [
			'albums',
			category,
			page,
			pageSize,
			sortField,
			sortDirection,
			seed, // Add seed to query key
			minYear || 'all',
			maxYear || 'all',
		],
		url: buildUrl(),
	});

	// Notify parent about loading state changes
	useEffect(() => {
		if (onIsLoadingChange) {
			onIsLoadingChange(isLoading);
		}
	}, [isLoading, onIsLoadingChange]);

	// Memoize albums to prevent unnecessary re-renders/effect runs
	const albums = useMemo(() => albumData?.albums || [], [albumData]);
	const pageCount = useMemo(() => albumData?.pageCount || 1, [albumData]); // Memoize pageCount

	// Effect to call onPageCountChange when pageCount changes
	useEffect(() => {
		onPageCountChange(pageCount);
	}, [pageCount, onPageCountChange]);

	// Build URL for slideshow thumbnails
	const slideshowUrl = useMemo(() => {
		if (!category) return '';
		let apiUrl = `/api/galeria/gallery-items/random?limit=5`;
		if (category === 'secret') {
			apiUrl += '&onlySecret=true';
		} else if (category === 'classificar') {
			apiUrl += '&onlyProvisional=true';
		} else if (category !== 'tots') {
			apiUrl += `&unitat=${encodeURIComponent(category)}`;
		}
		if (minYear) apiUrl += `&minYear=${minYear}`;
		if (maxYear) apiUrl += `&maxYear=${maxYear}`;
		return apiUrl;
	}, [category, minYear, maxYear]);

	// Fetch random thumbnails for the "All Photos" card slideshow via TanStack Query
	const {
		data: slideshowData,
		isLoading: isSlideshowLoading,
	} = useFetch<{
		mediaItems: MediaItem[];
		totalCount?: number;
	}>({
		queryKey: ['album-slideshow-thumbnails', category, minYear || 'all', maxYear || 'all'],
		url: slideshowUrl,
	});

	// Derive randomThumbnailUrls and totalCount from query data
	useEffect(() => {
		if (slideshowData?.mediaItems && slideshowData.mediaItems.length > 0) {
			const urls = slideshowData.mediaItems
				.map((item) => item.thumbnail_path)
				.filter((url) => !!url);
			setRandomThumbnailUrls(urls);
		} else {
			setRandomThumbnailUrls([]);
		}
		if (typeof slideshowData?.totalCount === 'number') {
			setCategoryTotalCount(slideshowData.totalCount);
		} else {
			setCategoryTotalCount(null);
		}
	}, [slideshowData]);

	// Thumbnails are loading while the query is fetching
	const isThumbnailsLoading = isSlideshowLoading;

	// Delete album mutation
	const deleteMutation = useSimpleMutation<string>({
		method: 'delete',
		url: (data) => `/api/galeria/albums/${data}`,
		queriesToInvalidate: [
			// Use 'all' as sentinel value to match the query key format
			[
				'albums',
				category,
				page,
				pageSize,
				sortField,
				sortDirection,
				seed,
				minYear || 'all',
				maxYear || 'all',
			],
			['albums', 'tots'],
			['pictures', category === 'tots' ? 'totes' : `unitat${category}`],
			['album-slideshow-thumbnails', category, minYear || 'all', maxYear || 'all'],
			// Thumbnails of the deleted album may live in the category card
			// slideshows — don't leave those stale.
			['category-thumbnails'],
		],
	});

	const handleDelete = (e: React.MouseEvent, albumId: string) => {
		e.stopPropagation(); // Prevent opening the album
		if (
			window.confirm(
				"Segur que vols eliminar l'àlbum i totes les seves imatges?"
			)
		) {
			deleteMutation.mutate(albumId);
		}
	};

	const handleAlbumClick = (albumId: string, albumName: string) => {
		onAlbumSelect(albumId, albumName);
	};

	// Handle special case for "All Photos"
	const handleAllPhotosClick = () => {
		onAlbumSelect('tots', 'Totes les fotos');
	};

	// Handle click for "All Photos of Unitat"
	const handleUnitatAllPhotosClick = () => {
		// Use "unitat:" prefix to distinguish from album IDs
		onAlbumSelect(
			`unitat:${category}`,
			`Totes les fotos ${
				category === 'agrupament' ? "d'" : 'de '
			}${category}`
		);
	};

	// Removed local handlers handlePageChange, handlePageSizeChange
	// They are now passed as props: onPageChange, onPageSizeChange

	if (isLoading) {
		return (
			<LoadingMessage>
				<LoadingGif size="large" />
			</LoadingMessage>
		);
	}

	if (error) {
		return <ErrorMessage>Error: {extractErrorMessage(error)}</ErrorMessage>;
	}

	if (!albumData) {
		// Check albumData instead of albums for initial load state
		return (
			<ErrorMessage>No s&apos;han pogut carregar els àlbums</ErrorMessage>
		);
	}

	// Handlers are now defined above the early returns
	return (
		<>
			{isAuthenticated && category !== 'tots' && category !== 'classificar' && (
				<S.CreateAlbumButton
					to={`/galeria/albums/${category}/nou`}
					style={{ marginBottom: '20px' }}
				>
					Crear nou àlbum
				</S.CreateAlbumButton>
			)}
			<S.Grid>
				{/* Pseudo-album card for "All Photos" or "All Unitat Photos" */}
				{/* Don't render this card in secret or classificar categories */}
				{category && category !== 'secret' && category !== 'classificar' && (
					<S.AlbumCard
						onClick={
							category === 'tots'
								? handleAllPhotosClick
								: handleUnitatAllPhotosClick
						}
					>
						{/* Use optional chaining and provide fallback */}
						<SlideshowThumbnail
							images={randomThumbnailUrls}
							interval={6000}
							isLoading={isThumbnailsLoading}
						/>
						{/* Erroneous closing bracket removed */}
						<S.AlbumInfo
						// Removed inline style causing excessive height
						>
							<S.AlbumTitle>
								{category === 'tots'
									? 'Totes les fotos'
									: `Totes les fotos ${
											category === 'agrupament'
												? "d'"
												: 'de '
									  }${category}`}
							</S.AlbumTitle>
							{/* Add spacer div to push title up */}
							<div style={{ flexGrow: 1 }}></div>
							{/* Display total count if available */}
							{categoryTotalCount !== null && (
								<S.AlbumCount
									style={{
										textAlign: 'left',
										marginTop: '5px',
									}}
								>
									{' '}
									{/* Adjust style */}
									{categoryTotalCount} fotos i videos
								</S.AlbumCount>
							)}
						</S.AlbumInfo>
					</S.AlbumCard>
				)}

				{/* "Totes les fotos privades" pseudo-album — same visual style as regular album cards */}
				{showAllPrivatePseudoAlbum && (
					<S.AlbumCard
						onClick={() =>
							onAlbumSelect(
								'all-private',
								'Totes les fotos privades'
							)
						}
					>
						<SlideshowThumbnail
							images={randomThumbnailUrls}
							interval={6000}
							isLoading={isThumbnailsLoading}
						/>
						<S.AlbumInfo>
							<S.AlbumTitle>Totes les fotos privades</S.AlbumTitle>
							<div style={{ flexGrow: 1 }}></div>
							{categoryTotalCount !== null && (
								<S.AlbumCount
									style={{
										textAlign: 'left',
										marginTop: '5px',
									}}
								>
									{categoryTotalCount} fotos i videos
								</S.AlbumCount>
							)}
						</S.AlbumInfo>
					</S.AlbumCard>
				)}

				{/* Regular albums; when sorted by school year, insert a
				    separator header whenever the year group changes */}
				{albums.map((album, idx) => {
					const showYearSeparator =
						sortField === 'schoolYear' &&
						(idx === 0 ||
							(albums[idx - 1].schoolYear || null) !==
								(album.schoolYear || null));
					return (
					<Fragment key={album._id}>
					{showYearSeparator && sortField === 'schoolYear' && (
						<S.SchoolYearSeparator>
							<span>
								{album.schoolYear
									? `Curs ${album.schoolYear}`
									: 'Curs desconegut'}
							</span>
						</S.SchoolYearSeparator>
					)}
					<S.AlbumCard
						onClick={() => handleAlbumClick(album._id, album.name)}
					>
						<ThumbnailImage
							src={album.sample_image}
							alt={album.name}
						/>

						<S.AlbumInfo>
							<S.AlbumTitle>{album.name}</S.AlbumTitle>
							{/* Add spacer div back to push meta row to the bottom */}
							<div style={{ flexGrow: 1 }}></div>

							{/* School Year Info ("Curs desconegut" when unknown) */}
							{(
								<div
									style={{
										fontSize: '0.9rem',
										color: '#666',
										marginBottom: '4px',
									}}
								>
									{album.schoolYear || 'Curs desconegut'}
								</div>
							)}

							{/* Wrap Date and Count in the new Meta Row */}
							<S.AlbumMetaRow>
								{/* Swapped Date and Count */}
								<S.AlbumCount>
									{album.isProcessing ? (
										<span style={{ color: '#f39c12' }}>
											Processant...
										</span>
									) : (
										`${album.picture_count} fotos i videos`
									)}
								</S.AlbumCount>
								<S.AlbumDate style={{ margin: 0 }}>
									{' '}
									{/* Remove margin from date */}
									{new Date(
										album.upload_date
									).toLocaleDateString('ca-ES')}
								</S.AlbumDate>
							</S.AlbumMetaRow>
						</S.AlbumInfo>

						{isAuthenticated && (
							<S.ActionButtons>
								<S.ActionButton
									onClick={(e) => handleDelete(e, album._id)}
								>
									Eliminar
								</S.ActionButton>
							</S.ActionButtons>
						)}
					</S.AlbumCard>
					</Fragment>
					);
				})}
			</S.Grid>

			{albums.length === 0 && (
				<S.EmptyState>
					<h3>No hi ha àlbums públics en aquesta categoria</h3>
				</S.EmptyState>
			)}
		</>
	);
};

export default AlbumGrid;
